import React, { useEffect, useState, useMemo } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { ArrowLeft, ShoppingCart, Search, X, Plus, Check, ChevronRight, Ticket } from 'lucide-react';
import { useTab } from 'context/restaurant/TabContext';
import { useCart } from 'context/restaurant/CartContext';
import { useUser } from 'context/user/User';
import { useCashier } from 'context/restaurant/CashierContext';
import menuApi from 'api/requests/menu';
import posEventsApi from 'api/requests/posEvents';
import ProductDetailSheet from '../components/ProductDetailSheet';
import CartSheet from '../components/CartSheet';
import TicketDetailSheet from '../components/TicketDetailSheet';
import colors from 'theme/colors';
import breakpoints from 'theme/breakpoints';

function formatPrice(value) {
    return Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

/* ───── Frequency tracking helpers (E) ───── */

function trackProductFrequency(productId) {
    try {
        const key = 'waiter_product_freq';
        const freq = JSON.parse(localStorage.getItem(key) || '{}');
        freq[productId] = (freq[productId] || 0) + 1;
        localStorage.setItem(key, JSON.stringify(freq));
    } catch {}
}

function getFrequentProductIds(limit = 10) {
    try {
        const key = 'waiter_product_freq';
        const freq = JSON.parse(localStorage.getItem(key) || '{}');
        return Object.entries(freq)
            .sort((a, b) => b[1] - a[1])
            .slice(0, limit)
            .map(([id]) => Number(id));
    } catch { return []; }
}


export { trackProductFrequency };

export default function MenuBrowserScreen() {
    const { activeTab, setActiveTab } = useTab();
    const { cartItems, addItem } = useCart();
    const { user } = useUser();
    const { posEvent } = useCashier();
    const slug = user?.establishments?.[0]?.slug;

    const TICKETS_CATEGORY = '__tickets__';

    const [menu, setMenu] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeCategory, setActiveCategory] = useState(null);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [showCart, setShowCart] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [quickAddFeedback, setQuickAddFeedback] = useState(null);
    const [freqTrigger, setFreqTrigger] = useState(0);
    const [eventTickets, setEventTickets] = useState([]);
    const [selectedTicket, setSelectedTicket] = useState(null);

    useEffect(() => {
        if (!slug) return;
        menuApi.getMenu(slug)
            .then(({ data }) => {
                const items = data.data || [];
                setMenu(items);
                if (items.length) setActiveCategory(items[0].id);
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [slug]);

    useEffect(() => {
        if (!slug || !posEvent) {
            setEventTickets([]);
            return;
        }
        posEventsApi.listTickets(slug, posEvent.id)
            .then(({ data }) => {
                setEventTickets(Array.isArray(data) ? data : (data.data || []));
            })
            .catch(() => setEventTickets([]));
    }, [slug, posEvent]);

    // Count items in cart per product
    const cartCountMap = useMemo(() => {
        const map = {};
        cartItems.forEach(item => {
            const id = item.product?.id;
            if (!id) return;
            map[id] = (map[id] || 0) + item.quantity;
        });
        return map;
    }, [cartItems]);


    // Frequent products (E)
    const frequentProducts = useMemo(() => {
        const ids = getFrequentProductIds(10);
        if (!ids.length || !menu.length) return [];
        const allProducts = menu.flatMap(cat => cat.products || []);
        return ids.map(id => allProducts.find(p => p.id === id)).filter(Boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [menu, freqTrigger]);

    // Search results across all categories
    const searchResults = useMemo(() => {
        if (!searchQuery.trim()) return [];
        const q = searchQuery.toLowerCase().trim();
        const results = [];
        menu.forEach(cat => {
            cat.products?.forEach(product => {
                if (
                    product.name.toLowerCase().includes(q) ||
                    (product.description && product.description.toLowerCase().includes(q))
                ) {
                    results.push({ ...product, categoryName: cat.name });
                }
            });
        });
        return results;
    }, [menu, searchQuery]);

    const isSearching = searchQuery.trim().length > 0;

    // Quick-add handler (D)
    function handleQuickAdd(product, e) {
        if (e && e.stopPropagation) e.stopPropagation();
        if (product.option_groups?.length > 0) {
            setSelectedProduct(product);
            return;
        }
        const discountedPrice = Number(product.base_price) - Number(product.promotion?.discount_amount || 0);
        addItem({
            product,
            selections: {},
            quantity: 1,
            totalPrice: discountedPrice,
            observation: null,
        });
        trackProductFrequency(product.id);
        setFreqTrigger(t => t + 1);
        setQuickAddFeedback(product.id);
        setTimeout(() => setQuickAddFeedback(null), 600);
    }

    function handleTicketQuickAdd(ticket) {
        addItem({
            product: { id: ticket.id, name: ticket.name, base_price: ticket.base_price },
            selections: {},
            quantity: 1,
            totalPrice: Number(ticket.base_price || 0),
        });
    }

    if (loading) {
        return (
            <Screen>
                <LoadingCenter>
                    <Spinner />
                </LoadingCenter>
            </Screen>
        );
    }

    const currentCategory = menu.find(c => c.id === activeCategory);
    const cartTotal = cartItems.reduce((s, i) => s + i.totalPrice * i.quantity, 0);

    const renderProductRow = (product, index, showCategory = false) => {
        const isFeedback = quickAddFeedback === product.id;

        return (
            <ProductRow
                key={product.id}
                $index={index}
                onClick={() => setSelectedProduct(product)}
            >
                <ProductImageWrap>
                    {product.image
                        ? <ProductImage
                            src={product.image.thumb}
                            alt={product.name}
                            onError={e => { e.target.onerror = null; e.target.src = '/assets/images/placeholder-events.png'; }}
                          />
                        : <ProductImagePlaceholder />
                    }
                    {cartCountMap[product.id] > 0 && (
                        <InCartBadge>{cartCountMap[product.id]}</InCartBadge>
                    )}
                </ProductImageWrap>
                <ProductInfo>
                    {showCategory && product.categoryName && (
                        <ProductCategoryTag>{product.categoryName}</ProductCategoryTag>
                    )}
                    <ProductName>{product.name}</ProductName>
                    {product.description && (
                        <ProductDesc>{product.description}</ProductDesc>
                    )}
                    <ProductPriceRow>
                        {product.promotion?.discount_amount > 0 && (
                            <ProductOriginalPrice>{formatPrice(product.base_price)}</ProductOriginalPrice>
                        )}
                        <ProductPrice $discounted={!!product.promotion?.discount_amount}>
                            {formatPrice(product.promotion?.discount_amount > 0
                                ? Number(product.base_price) - Number(product.promotion.discount_amount)
                                : product.base_price
                            )}
                        </ProductPrice>
                        {product.promotion && (
                            <MenuPromoBadge>
                                {product.promotion.type === 'bundle'
                                    ? product.promotion.name
                                    : product.promotion.discount_amount > 0
                                        ? `-${formatPrice(product.promotion.discount_amount)}`
                                        : product.promotion.name
                                }
                            </MenuPromoBadge>
                        )}
                    </ProductPriceRow>
                </ProductInfo>
                <QuickAddBtn
                    onClick={(e) => handleQuickAdd(product, e)}
                    $feedback={isFeedback}
                    aria-label={`Adicionar ${product.name}`}
                >
                    {isFeedback
                        ? <Check size={18} color="#fff" strokeWidth={3} />
                        : <Plus size={18} color={colors.occasionalPurple} />
                    }
                </QuickAddBtn>
            </ProductRow>
        );
    };

    return (
        <Screen>
            {/* Header */}
            <Header>
                <BackBtn onClick={() => setActiveTab(null)} aria-label="Voltar">
                    <ArrowLeft size={20} color={colors.nightDark} />
                </BackBtn>
                <HeaderInfo>
                    <TabChip>
                        {activeTab?.table_identifier}
                        {activeTab?.person_name && activeTab?.table_identifier && (
                            <TabChipSep> &middot; </TabChipSep>
                        )}
                        {activeTab?.person_name}
                    </TabChip>
                </HeaderInfo>
                <CartIconBtn onClick={() => setShowCart(true)} aria-label="Ver pedido">
                    <ShoppingCart
                        size={20}
                        color={cartItems.length > 0 ? colors.occasionalPurple : colors.night}
                    />
                    {cartItems.length > 0 && (
                        <CartBadge>{cartItems.reduce((s, i) => s + i.quantity, 0)}</CartBadge>
                    )}
                </CartIconBtn>
            </Header>

            {/* Always-visible Search Bar (F) */}
            <SearchBarFixed>
                <SearchIcon>
                    <Search size={16} color={colors.nightLight} />
                </SearchIcon>
                <SearchInput
                    placeholder="Buscar no cardápio..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                    <SearchClear onClick={() => setSearchQuery('')}>
                        <X size={14} color={colors.night} />
                    </SearchClear>
                )}
            </SearchBarFixed>

            {/* Frequent Products Section (E) */}
            {!isSearching && frequentProducts.length > 0 && (
                <FrequentSection>
                    <FrequentTitle>Mais pedidos</FrequentTitle>
                    <FrequentScroll>
                        {frequentProducts.map(product => (
                            <FrequentCard
                                key={product.id}
                                onClick={() => handleQuickAdd(product)}
                            >
                                {product.image ? (
                                    <FrequentImage
                                        src={product.image.thumb}
                                        alt={product.name}
                                        onError={e => { e.target.onerror = null; e.target.src = '/assets/images/placeholder-events.png'; }}
                                    />
                                ) : (
                                    <FrequentImagePlaceholder />
                                )}
                                <FrequentName>{product.name}</FrequentName>
                                <FrequentBottom>
                                    <FrequentPrice>{formatPrice(product.base_price)}</FrequentPrice>
                                    {cartCountMap[product.id] > 0 && (
                                        <FrequentBadge>{cartCountMap[product.id]}</FrequentBadge>
                                    )}
                                </FrequentBottom>
                            </FrequentCard>
                        ))}
                    </FrequentScroll>
                </FrequentSection>
            )}

            {/* Category Pill Bar (hidden during search) */}
            {!isSearching && (
                <CategoryBar>
                    {eventTickets.length > 0 && (
                        <CategoryPill
                            key={TICKETS_CATEGORY}
                            $active={activeCategory === TICKETS_CATEGORY}
                            onClick={() => setActiveCategory(TICKETS_CATEGORY)}
                        >
                            🎟 Ingressos
                        </CategoryPill>
                    )}
                    {menu.map(cat => (
                        <CategoryPill
                            key={cat.id}
                            $active={activeCategory === cat.id}
                            onClick={() => setActiveCategory(cat.id)}
                        >
                            {cat.name}
                            {cat.products?.length > 0 && (
                                <CatCount $active={activeCategory === cat.id}>
                                    {cat.products.length}
                                </CatCount>
                            )}
                        </CategoryPill>
                    ))}
                </CategoryBar>
            )}

            {/* Product Area */}
            <ProductArea>
                {activeCategory === TICKETS_CATEGORY ? (
                    eventTickets.length === 0 ? (
                        <EmptyCategory><span>Nenhum ingresso disponível</span></EmptyCategory>
                    ) : (
                        <ProductList>
                            <EventSection>
                                <EventSectionTitle>🎟 {posEvent?.name}</EventSectionTitle>
                                {eventTickets.map(ticket => (
                                    <TicketCard key={ticket.id} onClick={() => setSelectedTicket(ticket)}>
                                        <TicketIconWrap>
                                            <Ticket size={28} color={colors.occasionalPurple} />
                                            {cartCountMap[ticket.id] > 0 && (
                                                <InCartBadge>{cartCountMap[ticket.id]}</InCartBadge>
                                            )}
                                        </TicketIconWrap>
                                        <ProductInfo>
                                            <ProductName>{ticket.name}</ProductName>
                                            {ticket.description && (
                                                <ProductDesc>{ticket.description}</ProductDesc>
                                            )}
                                            <ProductPrice>
                                                {Number(ticket.base_price || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                                {ticket.available != null && (
                                                    <TicketAvailable> · {ticket.available} disp.</TicketAvailable>
                                                )}
                                            </ProductPrice>
                                        </ProductInfo>
                                        <QuickAddBtn
                                            onClick={(e) => { e.stopPropagation(); handleTicketQuickAdd(ticket); }}
                                            aria-label={`Adicionar ${ticket.name}`}
                                        >
                                            <Plus size={18} color={colors.occasionalPurple} />
                                        </QuickAddBtn>
                                    </TicketCard>
                                ))}
                            </EventSection>
                        </ProductList>
                    )
                ) : isSearching ? (
                    searchResults.length === 0 ? (
                        <EmptySearch>
                            <Search size={28} color={colors.nightLight} />
                            <EmptySearchText>Nenhum produto encontrado</EmptySearchText>
                        </EmptySearch>
                    ) : (
                        <ProductList>
                            <SearchResultCount>
                                {searchResults.length} resultado{searchResults.length !== 1 ? 's' : ''}
                            </SearchResultCount>
                            {searchResults.map((product, index) => renderProductRow(product, index, true))}
                        </ProductList>
                    )
                ) : (
                    !currentCategory?.products?.length ? (
                        <EmptyCategory>
                            <span>Nenhum produto nesta categoria</span>
                        </EmptyCategory>
                    ) : (
                        <ProductList>
                            {currentCategory.products.map((product, index) => renderProductRow(product, index))}
                        </ProductList>
                    )
                )}
            </ProductArea>

            {/* Sticky Cart Footer */}
            {cartItems.length > 0 && (
                <CartFooter onClick={() => setShowCart(true)}>
                    <CartFooterBadge>
                        {cartItems.reduce((s, i) => s + i.quantity, 0)}
                    </CartFooterBadge>
                    <CartFooterLabel>Ver pedido</CartFooterLabel>
                    <CartFooterTotal>{formatPrice(cartTotal)}</CartFooterTotal>
                </CartFooter>
            )}

            {/* Product Detail Sheet */}
            {selectedProduct && (
                <ProductDetailSheet
                    product={selectedProduct}
                    onClose={() => setSelectedProduct(null)}
                />
            )}

            {/* Cart Sheet */}
            <CartSheet isOpen={showCart} onClose={() => setShowCart(false)} />

            {/* Ticket Detail Sheet */}
            <TicketDetailSheet
                isOpen={!!selectedTicket}
                onClose={() => setSelectedTicket(null)}
                ticket={selectedTicket}
                onAdd={(qty) => {
                    if (!selectedTicket) return;
                    addItem({
                        product: { id: selectedTicket.id, name: selectedTicket.name, base_price: selectedTicket.base_price },
                        selections: {},
                        quantity: qty,
                        totalPrice: Number(selectedTicket.base_price || 0),
                    });
                    setSelectedTicket(null);
                }}
            />
        </Screen>
    );
}

/* ───── Animations ───── */

const spin = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;
const fadeSlideIn = keyframes`from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); }`;
const popIn = keyframes`from { transform: scale(0); } to { transform: scale(1); }`;
const quickAddPop = keyframes`
    0% { transform: scale(1); }
    40% { transform: scale(1.25); }
    100% { transform: scale(1); }
`;

/* ───── Layout ───── */

const Screen = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    background: #f5f5f7;
`;

const LoadingCenter = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 300px;
`;

const Spinner = styled.div`
    width: 32px;
    height: 32px;
    border: 3px solid ${colors.neutralDark};
    border-top-color: ${colors.occasionalPurple};
    border-radius: 50%;
    animation: ${spin} 0.7s linear infinite;
`;

/* ───── Header ───── */

const Header = styled.div`
    display: flex;
    align-items: center;
    padding: 10px 12px 10px 8px;
    gap: 8px;
    background: #fff;
    flex-shrink: 0;
    min-height: 56px;
`;

const BackBtn = styled.button`
    background: none;
    border: none;
    padding: 10px;
    cursor: pointer;
    display: flex;
    border-radius: 8px;
    flex-shrink: 0;
    &:hover { background: ${colors.neutralDark}; }
`;

const HeaderInfo = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
`;

const TabChip = styled.span`
    font-weight: 700;
    font-size: 15px;
    color: ${colors.nightDark};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const TabChipSep = styled.span`
    color: ${colors.nightLight};
    font-weight: 400;
`;

const CartIconBtn = styled.button`
    background: none;
    border: none;
    padding: 10px;
    cursor: pointer;
    display: flex;
    position: relative;
    border-radius: 8px;
    flex-shrink: 0;
    &:hover { background: ${colors.neutralDark}; }
`;

const CartBadge = styled.span`
    position: absolute;
    top: 2px;
    right: 2px;
    background: ${colors.occasionalPurple};
    color: #fff;
    border-radius: 50%;
    min-width: 17px;
    height: 17px;
    font-size: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    padding: 0 4px;
    animation: ${popIn} 0.2s ease;
`;

/* ───── Always-visible Search (F) ───── */

const SearchBarFixed = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 8px 12px 8px;
    background: #fff;
    border: 1.5px solid ${colors.neutralDark};
    border-radius: 10px;
    padding: 0 12px;
    min-height: 40px;
    flex-shrink: 0;
    transition: border-color 0.15s;
    &:focus-within { border-color: ${colors.occasionalPurple}; }
`;

const SearchIcon = styled.div`
    display: flex;
    flex-shrink: 0;
`;

const SearchInput = styled.input`
    flex: 1;
    background: none;
    border: none;
    outline: none;
    font-size: 15px;
    color: ${colors.nightDark};
    &::placeholder { color: ${colors.nightLight}; }
`;

const SearchClear = styled.button`
    background: ${colors.neutralDark};
    border: none;
    padding: 4px;
    cursor: pointer;
    display: flex;
    border-radius: 6px;
    flex-shrink: 0;
    &:hover { background: #e0e0e0; }
`;

const SearchResultCount = styled.span`
    font-size: 12px;
    font-weight: 600;
    color: ${colors.nightLight};
    text-transform: uppercase;
    letter-spacing: 0.5px;
    padding: 0 4px 8px;
`;

const EmptySearch = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    gap: 12px;
`;

const EmptySearchText = styled.span`
    font-size: 15px;
    color: ${colors.nightLight};
`;

/* ───── Frequent Products (E) ───── */

const FrequentSection = styled.div`
    flex-shrink: 0;
    padding: 4px 0 8px;
    background: #fff;
    border-bottom: 1px solid ${colors.neutralDark};
`;

const FrequentTitle = styled.span`
    display: block;
    font-size: 12px;
    font-weight: 700;
    color: ${colors.nightLight};
    text-transform: uppercase;
    letter-spacing: 0.5px;
    padding: 0 16px 8px;
`;

const FrequentScroll = styled.div`
    display: flex;
    gap: 10px;
    padding: 0 16px;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    &::-webkit-scrollbar { display: none; }
`;

const FrequentCard = styled.div`
    flex-shrink: 0;
    width: 110px;
    background: #f5f5f7;
    border-radius: 12px;
    overflow: hidden;
    cursor: pointer;
    transition: all 0.15s;
    border: 1.5px solid transparent;
    &:hover { border-color: ${colors.occasionalPurple}30; }
    &:active { transform: scale(0.97); }
`;

const FrequentImage = styled.img`
    width: 100%;
    height: 70px;
    object-fit: cover;
`;

const FrequentImagePlaceholder = styled.div`
    width: 100%;
    height: 70px;
    background: linear-gradient(135deg, ${colors.neutralDark} 0%, #e8e8e8 100%);
`;

const FrequentName = styled.span`
    display: block;
    font-size: 12px;
    font-weight: 600;
    color: ${colors.nightDark};
    padding: 6px 8px 2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const FrequentBottom = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 8px 8px;
`;

const FrequentPrice = styled.span`
    font-size: 12px;
    font-weight: 700;
    color: ${colors.occasionalPurple};
`;

const FrequentBadge = styled.span`
    background: ${colors.occasionalPurple};
    color: #fff;
    border-radius: 6px;
    min-width: 18px;
    height: 18px;
    font-size: 10px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 4px;
    animation: ${popIn} 0.2s ease;
`;

/* ───── Category Bar ───── */

const CategoryBar = styled.div`
    display: flex;
    gap: 8px;
    padding: 10px 16px;
    overflow-x: auto;
    background: #fff;
    border-bottom: 1px solid ${colors.neutralDark};
    flex-shrink: 0;
    -webkit-overflow-scrolling: touch;
    &::-webkit-scrollbar { display: none; }
`;

const CategoryPill = styled.button`
    flex-shrink: 0;
    padding: 7px 16px;
    border-radius: 20px;
    border: 1.5px solid ${({ $active }) => $active ? colors.occasionalPurple : colors.neutralDark};
    background: ${({ $active }) => $active ? colors.occasionalPurple : '#fff'};
    color: ${({ $active }) => $active ? '#fff' : colors.night};
    font-size: 13px;
    font-weight: ${({ $active }) => $active ? '600' : '400'};
    cursor: pointer;
    transition: all 0.15s;
    display: flex;
    align-items: center;
    gap: 6px;
`;

const CatCount = styled.span`
    font-size: 11px;
    font-weight: 700;
    background: ${({ $active }) => $active ? 'rgba(255,255,255,0.25)' : colors.neutralDark};
    color: ${({ $active }) => $active ? '#fff' : colors.nightLight};
    padding: 1px 6px;
    border-radius: 6px;
`;

/* ───── Products ───── */

const ProductArea = styled.div`
    flex: 1;
    overflow-y: auto;
    padding: 12px 12px 100px;
`;

const ProductList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const ProductRow = styled.div`
    display: flex;
    gap: 14px;
    background: #fff;
    border-radius: 12px;
    border: 1px solid ${colors.neutralDark};
    padding: 12px;
    cursor: pointer;
    box-shadow: 0 1px 4px rgba(0,0,0,0.06);
    transition: all 0.15s;
    animation: ${fadeSlideIn} 0.25s ease both;
    animation-delay: ${({ $index }) => $index != null ? Math.min($index * 0.03, 0.25) : 0}s;
    align-items: center;
    &:hover { box-shadow: 0 3px 10px rgba(0,0,0,0.12); }
    &:active { transform: scale(0.98); }
`;

const ProductImageWrap = styled.div`
    width: 72px;
    height: 72px;
    border-radius: 10px;
    overflow: hidden;
    flex-shrink: 0;
    position: relative;
`;

const ProductImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

const ProductImagePlaceholder = styled.div`
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, ${colors.neutralDark} 0%, #e8e8e8 100%);
`;

const InCartBadge = styled.div`
    position: absolute;
    top: 4px;
    right: 4px;
    background: ${colors.occasionalPurple};
    color: #fff;
    border-radius: 6px;
    min-width: 20px;
    height: 20px;
    font-size: 11px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 4px;
    animation: ${popIn} 0.2s ease;
    box-shadow: 0 2px 6px rgba(0,0,0,0.2);
`;

const ProductInfo = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 0;
    justify-content: center;
`;

const ProductCategoryTag = styled.span`
    font-size: 10px;
    font-weight: 600;
    color: ${colors.nightLight};
    text-transform: uppercase;
    letter-spacing: 0.5px;
`;

const ProductName = styled.span`
    font-weight: 700;
    font-size: 14px;
    color: ${colors.nightDark};
    line-height: 1.3;
`;

const ProductDesc = styled.span`
    font-size: 11px;
    color: ${colors.nightLight};
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
`;

const ProductPriceRow = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
    margin-top: 2px;
`;

const ProductOriginalPrice = styled.span`
    font-size: 12px;
    color: ${colors.nightLight};
    text-decoration: line-through;
    font-variant-numeric: tabular-nums;
`;

const ProductPrice = styled.span`
    font-size: 14px;
    font-weight: 700;
    color: ${({ $discounted }) => $discounted ? colors.occasionalGreen : colors.occasionalPurple};
    font-variant-numeric: tabular-nums;
`;

const MenuPromoBadge = styled.span`
    padding: 2px 7px;
    border-radius: 20px;
    background: #deffed;
    color: #1ea664;
    font-size: 11px;
    font-weight: 700;
    line-height: 1;
    align-self: center;
`;

/* ───── Quick Add Button (D) ───── */

const QuickAddBtn = styled.button`
    width: 44px;
    height: 44px;
    border-radius: 50%;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.15s;
    border: 2px solid ${({ $feedback }) => $feedback ? colors.success : colors.occasionalPurple};
    background: ${({ $feedback }) => $feedback ? colors.success : 'transparent'};
    ${({ $feedback }) => $feedback && css`
        animation: ${quickAddPop} 0.3s ease;
    `}
    &:hover:not([disabled]) {
        background: ${({ $feedback }) => $feedback ? colors.success : `${colors.occasionalPurple}15`};
    }
    &:active:not([disabled]) { transform: scale(0.9); }
`;


const EmptyCategory = styled.div`
    display: flex;
    justify-content: center;
    padding: 48px 0;
    font-size: 14px;
    color: ${colors.nightLight};
`;

/* ───── Cart Footer ───── */

const CartFooter = styled.div`
    position: fixed;
    bottom: 16px;
    left: 16px;
    right: 16px;
    @media screen and (min-width: ${breakpoints.lg}) {
        left: calc(260px + 16px);
    }
    background: ${colors.nightDark};
    border-radius: 14px;
    padding: 14px 20px;
    display: flex;
    align-items: center;
    gap: 12px;
    cursor: pointer;
    box-shadow: 0 8px 24px rgba(0,0,0,0.25);
    z-index: 20;
    transition: transform 0.1s;
    animation: ${fadeSlideIn} 0.3s ease;
    &:hover { transform: translateY(-1px); }
    &:active { transform: scale(0.99); }
`;

const CartFooterBadge = styled.div`
    background: ${colors.occasionalPurple};
    color: #fff;
    border-radius: 8px;
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    font-weight: 700;
    flex-shrink: 0;
`;

const CartFooterLabel = styled.span`
    flex: 1;
    color: #fff;
    font-weight: 700;
    font-size: 15px;
`;

const CartFooterTotal = styled.span`
    color: #fff;
    font-weight: 700;
    font-size: 15px;
`;

/* ───── Ticket / Event Rows ───── */

const EventSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 16px;
`;

const EventSectionTitle = styled.div`
    font-size: 13px;
    font-weight: 700;
    color: ${colors.nightDark};
    padding: 10px 0 8px;
    border-bottom: 1px solid ${colors.neutralDark};
    margin-bottom: 4px;
`;

const EventDate = styled.span`
    font-weight: 400;
    color: ${colors.nightLight};
`;

const TicketCard = styled.div`
    display: flex;
    gap: 14px;
    background: #fff;
    border-radius: 12px;
    border: 1px solid ${colors.neutralDark};
    padding: 12px;
    cursor: pointer;
    box-shadow: 0 1px 4px rgba(0,0,0,0.06);
    transition: all 0.15s;
    align-items: center;
    &:hover { box-shadow: 0 3px 10px rgba(0,0,0,0.12); }
    &:active { transform: scale(0.98); }
`;

const TicketIconWrap = styled.div`
    width: 72px;
    height: 72px;
    border-radius: 10px;
    background: ${colors.occasionalPurple}12;
    border: 1.5px dashed ${colors.occasionalPurple}40;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
`;

const TicketAvailable = styled.span`
    font-size: 12px;
    color: ${colors.nightLight};
    font-weight: 400;
`;

const TicketEmpty = styled.p`
    font-size: 13px;
    color: ${colors.nightLight};
    padding: 8px 0 4px;
    margin: 0;
`;
